// src/components/brandCustomization/PublicPageCustomization/PublicPageSelector.jsx
import React, { useState, useMemo } from 'react';
import { FaSearch, FaShoppingBag, FaQrcode, FaCheckCircle, FaGlobe } from 'react-icons/fa';

/**
 * PublicPageSelector - Selector visual de páginas públicas
 * Muestra un grid de cards pequeñas para seleccionar qué página personalizar
 */
const PublicPageSelector = ({
    pages,
    selectedPage,
    onSelect,
    formData = {}
}) => {
    const [searchQuery, setSearchQuery] = useState('');

    // Información de cada página
    const pageInfo = {
        findTicket: {
            name: 'Página de Búsqueda de Tarjeta',
            description: 'Permite a los usuarios encontrar su tarjeta por email',
            icon: FaSearch,
            color: 'blue'
        },
        productos: {
            name: 'Página de Productos',
            description: 'Catálogo público de productos de la marca',
            icon: FaShoppingBag,
            color: 'green'
        },
        qrRegister: {
            name: 'Página de Registro QR',
            description: 'Formulario de registro para nuevos clientes desde QR',
            icon: FaQrcode,
            color: 'purple'
        }
    };

    // Filtrar páginas según búsqueda
    const filteredPages = useMemo(() => {
        if (!searchQuery.trim()) return pages;

        const query = searchQuery.toLowerCase();
        return pages.filter(pageName => {
            const info = pageInfo[pageName];
            if (!info) return false;

            return (
                info.name.toLowerCase().includes(query) ||
                info.description.toLowerCase().includes(query) ||
                pageName.toLowerCase().includes(query)
            );
        });
    }, [pages, searchQuery]);

    return (
        <div className="mb-8">
            {/* Header con contador */}
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                        Selecciona una Página para Personalizar
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                        {pages.length} {pages.length === 1 ? 'página disponible' : 'páginas disponibles'}
                    </p>
                </div>
            </div>

            {/* Buscador (solo si hay más de 5 páginas) */}
            {pages.length > 5 && (
                <div className="mb-4">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar página..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>
                </div>
            )}

            {/* Grid de páginas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredPages.map(pageName => {
                    const info = pageInfo[pageName];
                    if (!info) return null;

                    const Icon = info.icon || FaGlobe;
                    const isSelected = selectedPage === pageName;
                    const pageData = formData[pageName] || {};
                    const isActive = pageData.isActive !== false;

                    // Determinar color según el tipo de página
                    const colorClasses = {
                        blue: {
                            selected: 'border-blue-500 bg-blue-50',
                            icon: 'bg-blue-100 text-blue-600',
                            text: 'text-blue-900'
                        },
                        green: {
                            selected: 'border-green-500 bg-green-50',
                            icon: 'bg-green-100 text-green-600',
                            text: 'text-green-900'
                        },
                        purple: {
                            selected: 'border-purple-500 bg-purple-50',
                            icon: 'bg-purple-100 text-purple-600',
                            text: 'text-purple-900'
                        }
                    };

                    const colors = colorClasses[info.color] || colorClasses.blue;

                    return (
                        <button
                            key={pageName}
                            type="button"
                            onClick={() => onSelect(pageName)}
                            className={`
                relative p-4 rounded-lg border-2 transition-all text-left
                ${isSelected
                                    ? `${colors.selected} shadow-md`
                                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                                }
              `}
                        >
                            {/* Indicador de selección */}
                            {isSelected && (
                                <div className="absolute top-2 right-2">
                                    <FaCheckCircle className={`${colors.text.replace('900', '500')}`} />
                                </div>
                            )}

                            {/* Icono */}
                            <div className={`
                w-12 h-12 rounded-lg flex items-center justify-center mb-3
                ${isSelected ? colors.icon : 'bg-gray-100'}
              `}>
                                <Icon className={`
                  text-xl
                  ${isSelected ? colors.text.replace('900', '600') : 'text-gray-600'}
                `} />
                            </div>

                            {/* Nombre */}
                            <h4 className={`
                font-semibold text-sm mb-1
                ${isSelected ? colors.text : 'text-gray-900'}
              `}>
                                {info.name}
                            </h4>

                            {/* Descripción */}
                            <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                                {info.description}
                            </p>

                            {/* Estado activo/inactivo y layout */}
                            <div className="flex items-center justify-between mt-2">
                                <div className="flex items-center gap-2">
                                    <span className={`
                    w-2 h-2 rounded-full
                    ${isActive ? 'bg-green-500' : 'bg-gray-300'}
                  `} />
                                    <span className="text-xs text-gray-500">
                                        {isActive ? 'Activo' : 'Inactivo'}
                                    </span>
                                </div>
                                {/* Layout actual */}
                                {pageData.layout && (
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                        {pageData.layout}
                                    </span>
                                )}
                            </div>

                            {/* Indicador si tiene personalización */}
                            <div className="mt-2">
                                {(pageData.texts || pageData.styles || pageData.layoutOptions) ? (
                                    <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded">
                                        Personalizado
                                    </span>
                                ) : (
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                        Por defecto
                                    </span>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Mensaje si no hay resultados */}
            {filteredPages.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                    <FaSearch className="mx-auto text-3xl mb-2 opacity-50" />
                    <p>No se encontraron páginas que coincidan con "{searchQuery}"</p>
                </div>
            )}
        </div>
    );
};

export default PublicPageSelector;

